import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Card,
  CardContent,
  Typography, 
  TextField, 
  Button, 
  Box, 
  Grid, 
  CircularProgress, 
  Tabs, 
  Tab, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  InputAdornment, 
  IconButton,
  Stack,
  Divider,
  Chip
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon, Settings as SettingsIcon, LocationOn as LocationOnIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import clienteService from '../services/clienteService';
import LocalidadSearch from '../components/LocalidadSearch';
import localidadService from '../services/localidadService';
import ListaPrecioSearch from '../components/ListaPrecioSearch';
import listaPrecioService, { ListaPrecio } from '../services/listaPrecioService';
import VendedorSearch from '../components/VendedorSearch';
import vendedorService, { Vendedor } from '../services/vendedorService';
import ImputableSearch from '../components/ImputableSearch';
import imputableService, { Imputable } from '../services/imputableService';
import UbicacionSearch from '../components/UbicacionSearch';
import { IUbicacion } from '../interfaces/Ubicacion';
import ubicacionService from '../services/ubicacionService';
import estrategiaService from '../services/estrategiaService';
import TransporteSearch from '../components/TransporteSearch';
import transporteService, { Transporte } from '../services/transporteService';
import RutaSearch from '../components/RutaSearch';
import rutaService, { Ruta } from '../services/rutaService';
import DireccionesClienteDialog from '../components/DireccionesClienteDialog';
import direccionClienteService from '../services/direccionClienteService';
import { DireccionCliente } from '../interfaces/DireccionCliente';

interface ClienteFormData {
  nroCliente: number;
  nombre: string;
  razonSocial?: string;
  direccion?: string;
  codigoCiudad?: number | null;
  nombreCiudad?: string;
  telefono1?: string;
  telefono2?: string;
  fax?: string;
  email?: string;
  emailPersonal?: string;
  whatsapp?: string;
  cuit?: string;
  dni?: string;
  tipoIva?: string;
  condVta?: string;
  credito?: number | null;
  nroLista?: string;
  contacto?: string;
  fechaAlta?: string | null; // ISO
  fechaBaja?: string | null; // ISO
  tipoCliente?: string;
  ctaMaestra?: number | null;
  nroCuenta?: number | null;
  deposito?: number;
  nroClienteExterno?: string;
  vendedor?: number | null;
  nroCobrador?: number | null;
  vtoComprob?: number | null;
  nroExpreso?: number | null;
  reDespacho?: number | null;
  porcDesc?: number | null;
  porcDesc2?: number | null;
  porcDesc3?: number | null;
  atributo1?: string;
  atributo2?: string;
  atributo3?: string;
  atributo4?: string;
  atributo5?: string;
  codigoDireccion?: string | null;
  codigoDireccionEnvio?: string | null;
  codigoDireccionResumen?: string | null;
  direccionEnvio?: string;
  codigoCiudadEnvio?: number | null;
  direccionResumen?: string;
  codigoCiudadResumen?: number | null;
  codigoRuta?: number | null;
}

const defaultForm: ClienteFormData = {
  nroCliente: 0,
  nombre: '',
  razonSocial: '',
  direccion: '',
  codigoCiudad: null,
  telefono1: '',
  telefono2: '',
  fax: '',
  email: '',
  emailPersonal: '',
  whatsapp: '',
  cuit: '',
  dni: '',
  tipoIva: '',
  condVta: 'EFECTIVO',
  credito: null,
  nroLista: '',
  contacto: '',
  fechaAlta: null,
  fechaBaja: null,
  tipoCliente: 'A',
  ctaMaestra: null,
  nroCuenta: null,
  deposito: 0,
  nroClienteExterno: '',
  vendedor: 1,
  nroCobrador: 1,
  vtoComprob: null,
  nroExpreso: null,
  reDespacho: null,
  porcDesc: null,
  porcDesc2: null,
  porcDesc3: null,
  atributo1: '',
  atributo2: '',
  atributo3: '',
  atributo4: '',
  atributo5: '',
  codigoDireccion: 'FISCAL',
  codigoDireccionEnvio: null,
  codigoDireccionResumen: null,
  direccionEnvio: '',
  codigoCiudadEnvio: null,
  direccionResumen: '',
  codigoCiudadResumen: null,
  codigoRuta: 1,
};

interface Localidad {
  codigoCiudad: number;
  nombreCiudad?: string;
  codigoProvincia?: number;
  codigoRuta?: number;
  codigoPostal?: string;
  nombreProvincia?: string;
}

const ClienteForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroCliente } = useParams();
  const [formData, setFormData] = useState<ClienteFormData>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [openLocalidadSearch, setOpenLocalidadSearch] = useState(false);
  const [localidadSeleccionada, setLocalidadSeleccionada] = useState<Localidad | null>(null);
  const [openListaPrecioSearch, setOpenListaPrecioSearch] = useState(false);
  const [listaPrecioSeleccionada, setListaPrecioSeleccionada] = useState<Pick<ListaPrecio, 'codigoLista' | 'descripcion'> | null>(null);
  const [openVendedorSearch, setOpenVendedorSearch] = useState(false);
  const [vendedorSeleccionado, setVendedorSeleccionado] = useState<Pick<Vendedor, 'nroVendedor' | 'nombreVendedor'> | null>(null);
  const [openCobradorSearch, setOpenCobradorSearch] = useState(false);
  const [cobradorSeleccionado, setCobradorSeleccionado] = useState<Pick<Vendedor, 'nroVendedor' | 'nombreVendedor'> | null>(null);
  const [openImputableSearch, setOpenImputableSearch] = useState(false);
  const [imputableSeleccionado, setImputableSeleccionado] = useState<Pick<Imputable, 'nroCuenta' | 'nomCuenta'> | null>(null);
  const [openUbicacionSearch, setOpenUbicacionSearch] = useState(false);
  const [ubicacionSeleccionada, setUbicacionSeleccionada] = useState<Pick<IUbicacion, 'nroUbicacion' | 'nombreUbicacion'> | null>(null);
  const [openTransporteSearch, setOpenTransporteSearch] = useState(false);
  const [transporteSeleccionado, setTransporteSeleccionado] = useState<Pick<Transporte, 'nroExpreso' | 'nombre' | 'nombreExpreso'> | null>(null);
  const [openRedespachoSearch, setOpenRedespachoSearch] = useState(false);
  const [redespachoSeleccionado, setRedespachoSeleccionado] = useState<Pick<Transporte, 'nroExpreso' | 'nombre' | 'nombreExpreso'> | null>(null);
  const [openRutaSearch, setOpenRutaSearch] = useState(false);
  const [rutaSeleccionada, setRutaSeleccionada] = useState<Pick<Ruta, 'codigoRuta' | 'nombreRuta'> | null>(null);
  const [openDireccionesDialog, setOpenDireccionesDialog] = useState(false);
  const [direcciones, setDirecciones] = useState<DireccionCliente[]>([]);
  const [direccionFacturacion, setDireccionFacturacion] = useState<DireccionCliente | null>(null);
  const [direccionEnvio, setDireccionEnvio] = useState<DireccionCliente | null>(null);
  const [direccionResumen, setDireccionResumen] = useState<DireccionCliente | null>(null);
  const [localidadesDirecciones, setLocalidadesDirecciones] = useState<Map<string, Localidad>>(new Map());

  const cleanString = (value?: string | null) => {
    if (value === null || value === undefined) return '';
    return `${value}`.trim();
  };

  const formatLocalidadLine = (loc: Localidad | null | undefined) => {
    if (!loc) return null;
    const ciudad = cleanString(loc.nombreCiudad);
    const prov = cleanString(loc.nombreProvincia);
    const cp = cleanString(loc.codigoPostal);
    if (!ciudad && !prov && !cp) return null;
    const cityPart = cp ? `${ciudad}${ciudad ? ' ' : ''}(${cp})`.trim() : ciudad;
    const provDistinct = prov && ciudad && prov.toLowerCase() !== ciudad.toLowerCase() ? prov : prov && !ciudad ? prov : '';
    const line = [cityPart, provDistinct].filter(Boolean).join(', ');
    return line || null;
  };

  const entregaIgualAFacturacion = useMemo(() => {
    const cf = cleanString(formData.codigoDireccion);
    const ce = cleanString(formData.codigoDireccionEnvio);
    return !ce || ce === cf;
  }, [formData.codigoDireccion, formData.codigoDireccionEnvio]);

  const resumenIgualAFacturacion = useMemo(() => {
    const cf = cleanString(formData.codigoDireccion);
    const cr = cleanString(formData.codigoDireccionResumen);
    return !cr || cr === cf;
  }, [formData.codigoDireccion, formData.codigoDireccionResumen]);

  const buildListaPrecioResumen = useCallback((lista?: { codigoLista?: string; descripcion?: string | null }) => {
    if (!lista || !lista.codigoLista) return null;
    return {
      codigoLista: cleanString(lista.codigoLista),
      descripcion: cleanString(lista.descripcion),
    };
  }, []);

  const toIso = (v?: string | null) => {
    if (!v) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(v))) return new Date(String(v) + 'T00:00:00Z').toISOString();
    const d = new Date(String(v));
    return isNaN(d.getTime()) ? null : d.toISOString();
  };

  const loadCliente = useCallback(async () => {
    if (!nroCliente) return;
    try {
      setLoading(true);
      const cli = await clienteService.getCliente(Number(nroCliente));
      setLocalidadSeleccionada(null);
      setListaPrecioSeleccionada(null);
      setVendedorSeleccionado(null);
      setCobradorSeleccionado(null);
      setImputableSeleccionado(null);
      setRutaSeleccionada(null);
      const nroListaNormalizada = cleanString(cli.nroLista);
      setFormData({
        nroCliente: cli.nroCliente,
        nombre: cleanString(cli.nombre),
        razonSocial: cleanString(cli.razonSocial),
        direccion: cleanString(cli.direccion),
        codigoCiudad: cli.codigoCiudad ?? null,
        telefono1: cleanString(cli.telefono1),
        telefono2: cleanString(cli.telefono2),
        fax: cleanString(cli.fax),
        email: cleanString(cli.email),
        emailPersonal: cleanString((cli as any).emailPersonal),
        whatsapp: cleanString((cli as any).whatsapp || cli.fax),
        cuit: cleanString(cli.cuit),
        dni: cleanString((cli as any).dni),
        tipoIva: cleanString(cli.tipoIva),
        condVta: cleanString(cli.condVta),
        credito: cli.credito ?? null,
        nroLista: nroListaNormalizada,
        contacto: cleanString(cli.contacto),
        fechaAlta: cli.fechaAlta ?? null,
        fechaBaja: cli.fechaBaja ?? null,
        tipoCliente: cleanString(cli.tipoCliente),
        ctaMaestra: cli.ctaMaestra ?? null,
        nroCuenta: cli.nroCuenta ?? null,
        deposito: cli.deposito ?? 0,
        nroClienteExterno: cleanString(cli.nroClienteExterno),
        vendedor: cli.vendedor ?? null,
        nroCobrador: cli.nroCobrador ?? null,
        vtoComprob: cli.vtoComprob ?? null,
        nroExpreso: cli.nroExpreso ? (typeof cli.nroExpreso === 'string' ? Number(cli.nroExpreso) : cli.nroExpreso) : null,
        reDespacho: cli.reDespacho ?? null,
        porcDesc: cli.porcDesc ?? null,
        porcDesc2: cli.porcDesc2 ?? null,
        porcDesc3: cli.porcDesc3 ?? null,
        atributo1: cleanString(cli.atributo1),
        atributo2: cleanString(cli.atributo2),
        atributo3: cleanString(cli.atributo3),
        atributo4: cleanString(cli.atributo4),
        atributo5: cleanString(cli.atributo5),
        codigoDireccion: cli.codigoDireccion ? cleanString(cli.codigoDireccion) : null,
        codigoDireccionEnvio: cli.codigoDireccionEnvio ? cleanString(cli.codigoDireccionEnvio) : null,
        codigoDireccionResumen: cli.codigoDireccionResumen ? cleanString(cli.codigoDireccionResumen) : null,
        direccionEnvio: cleanString((cli as any).direccionEnvio),
        codigoCiudadEnvio: (cli as any).codigoCiudadEnvio ?? null,
        direccionResumen: cleanString((cli as any).direccionResumen),
        codigoCiudadResumen: (cli as any).codigoCiudadResumen ?? null,
        codigoRuta: cli.codigoRuta ?? null,
      });
      
      // Localidad: GET /api/clientes/{id} no incluye nombreProvincia en ciudad; completar desde /api/localidades/{codigoCiudad}
      const codCiudadCliente = cli.ciudad?.codigoCiudad ?? cli.codigoCiudad;
      if (codCiudadCliente) {
        try {
          const localidad = await localidadService.getLocalidad(codCiudadCliente);
          setLocalidadSeleccionada(localidad);
        } catch (error) {
          if (cli.ciudad?.codigoCiudad) {
            setLocalidadSeleccionada({
              codigoCiudad: cli.ciudad.codigoCiudad,
              nombreCiudad: cleanString(cli.ciudad.nombreCiudad),
              codigoProvincia: cli.ciudad.codigoProvincia ?? undefined,
              codigoPostal: cleanString(cli.ciudad.codigoPostal),
            });
          }
          console.error('Error al cargar localidad:', error);
        }
      }
      
      // Lista de precios
      const listaDesdeApi = buildListaPrecioResumen(cli.listaPrecio);
      if (listaDesdeApi) {
        setListaPrecioSeleccionada(listaDesdeApi);
      } else if (nroListaNormalizada) {
        try {
          const listaPrecio = await listaPrecioService.getListaPrecio(nroListaNormalizada);
          setListaPrecioSeleccionada({
            codigoLista: listaPrecio.codigoLista,
            descripcion: listaPrecio.descripcion,
          });
        } catch (error) {
          // Si no se puede cargar la lista de precios, no es crítico
          console.error('Error al cargar lista de precios:', error);
        }
      }
      
      // Vendedor
      if (cli.vendedorInfo?.nroVendedor) {
        setVendedorSeleccionado({
          nroVendedor: cli.vendedorInfo.nroVendedor,
          nombreVendedor: cleanString(cli.vendedorInfo.nombreVendedor),
        });
      } else if (cli.vendedor) {
        try {
          const vendedor = await vendedorService.getVendedor(cli.vendedor);
          if (vendedor) {
            setVendedorSeleccionado({
              nroVendedor: vendedor.nroVendedor,
              nombreVendedor: vendedor.nombreVendedor,
            });
          }
        } catch (error) {
          // Si no se puede cargar el vendedor, no es crítico
          console.error('Error al cargar vendedor:', error);
        }
      }
      
      // Cobrador
      if (cli.cobradorInfo?.nroVendedor) {
        setCobradorSeleccionado({
          nroVendedor: cli.cobradorInfo.nroVendedor,
          nombreVendedor: cleanString(cli.cobradorInfo.nombreVendedor),
        });
      } else if (cli.nroCobrador) {
        try {
          const cobrador = await vendedorService.getVendedor(cli.nroCobrador);
          if (cobrador) {
            setCobradorSeleccionado({
              nroVendedor: cobrador.nroVendedor,
              nombreVendedor: cobrador.nombreVendedor,
            });
          }
        } catch (error) {
          console.error('Error al cargar cobrador:', error);
        }
      }
      
      // Cuenta contable (Imputable)
      if (cli.cuentaContable?.nroCuenta) {
        setImputableSeleccionado({
          nroCuenta: cli.cuentaContable.nroCuenta,
          nomCuenta: cleanString(cli.cuentaContable.nombreCuenta),
        });
      } else if (cli.nroCuenta) {
        try {
          const imputable = await imputableService.getImputable(cli.nroCuenta);
          if (imputable) {
            setImputableSeleccionado({
              nroCuenta: imputable.nroCuenta,
              nomCuenta: cleanString(imputable.nomCuenta),
            });
          }
        } catch (error) {
          console.error('Error al cargar cuenta contable:', error);
        }
      }
      
      // Ubicación (Depósito)
      if (cli.depositoInfo?.nroUbicacion) {
        setUbicacionSeleccionada({
          nroUbicacion: cli.depositoInfo.nroUbicacion,
          nombreUbicacion: cleanString(cli.depositoInfo.nombreUbicacion),
        });
      } else if (cli.deposito) {
        try {
          const ubicacion = await ubicacionService.getUbicacion(cli.deposito);
          if (ubicacion) {
            setUbicacionSeleccionada({
              nroUbicacion: ubicacion.nroUbicacion,
              nombreUbicacion: cleanString(ubicacion.nombreUbicacion),
            });
          }
        } catch (error) {
          console.error('Error al cargar ubicación:', error);
        }
      }
      
      // Transporte (nroExpreso)
      const transporteInfo = (cli as any).transporte;
      if (transporteInfo?.nroExpreso) {
        setTransporteSeleccionado({
          nroExpreso: transporteInfo.nroExpreso,
          nombre: cleanString(transporteInfo.nombre),
          nombreExpreso: cleanString(transporteInfo.nombreExpreso),
        });
      } else if (cli.nroExpreso) {
        try {
          const transporte = await transporteService.getTransporte(cli.nroExpreso);
          if (transporte) {
            setTransporteSeleccionado({
              nroExpreso: transporte.nroExpreso,
              nombre: cleanString(transporte.nombre),
              nombreExpreso: cleanString(transporte.nombreExpreso),
            });
          }
        } catch (error) {
          console.error('Error al cargar transporte:', error);
        }
      }
      
      // Cargar direcciones del cliente
      try {
        const direccionesData = await direccionClienteService.getDirecciones(cli.nroCliente);
        setDirecciones(direccionesData || []);
        
        // Cargar información de localidades para las direcciones
        const localidadesMap = new Map<string, Localidad>();
        for (const dir of direccionesData || []) {
          if (dir.codigoCiudad) {
            try {
              const loc = await localidadService.getLocalidad(dir.codigoCiudad);
              localidadesMap.set(cleanString(dir.codigoDireccion), loc);
            } catch (error) {
              // Si no se puede cargar la localidad, no es crítico
              console.error(`Error al cargar localidad ${dir.codigoCiudad}:`, error);
            }
          }
        }
        setLocalidadesDirecciones(localidadesMap);
        
        // Cargar direcciones asignadas desde el cliente
        // Los campos vienen de la BD y pueden tener espacios en blanco, así que los limpiamos
        const codigoDirFacturacion = cli.codigoDireccion ? cleanString(cli.codigoDireccion) : null;
        const codigoDirEnvio = cli.codigoDireccionEnvio ? cleanString(cli.codigoDireccionEnvio) : null;
        const codigoDirResumen = cli.codigoDireccionResumen ? cleanString(cli.codigoDireccionResumen) : null;
        
        if (codigoDirFacturacion) {
          const dir = direccionesData?.find(d => cleanString(d.codigoDireccion) === codigoDirFacturacion);
          setDireccionFacturacion(dir || null);
        } else {
          setDireccionFacturacion(null);
        }
        
        if (codigoDirEnvio) {
          const dir = direccionesData?.find(d => cleanString(d.codigoDireccion) === codigoDirEnvio);
          setDireccionEnvio(dir || null);
        } else {
          setDireccionEnvio(null);
        }
        
        if (codigoDirResumen) {
          const dir = direccionesData?.find(d => cleanString(d.codigoDireccion) === codigoDirResumen);
          setDireccionResumen(dir || null);
        } else {
          setDireccionResumen(null);
        }
      } catch (error) {
        console.error('Error al cargar direcciones:', error);
        // No es crítico si no se pueden cargar las direcciones
      }
      
      // Redespacho (reDespacho)
      if (cli.reDespacho) {
        try {
          const redespacho = await transporteService.getTransporte(cli.reDespacho);
          if (redespacho) {
            setRedespachoSeleccionado({
              nroExpreso: redespacho.nroExpreso,
              nombre: cleanString(redespacho.nombre),
              nombreExpreso: cleanString(redespacho.nombreExpreso),
            });
          }
        } catch (error) {
          console.error('Error al cargar redespacho:', error);
        }
      }
      
      // Ruta (codigoRuta)
      if (cli.codigoRuta) {
        try {
          const ruta = await rutaService.getRuta(cli.codigoRuta);
          if (ruta) {
            setRutaSeleccionada({
              codigoRuta: ruta.codigoRuta,
              nombreRuta: cleanString(ruta.nombreRuta),
            });
          }
        } catch (error) {
          console.error('Error al cargar ruta:', error);
        }
      }
    } catch (error) {
      enqueueSnackbar('Error al cargar el cliente', { variant: 'error' });
      navigate('/ventas/clientes');
    } finally {
      setLoading(false);
    }
  }, [nroCliente, enqueueSnackbar, navigate, buildListaPrecioResumen]);

  useEffect(() => {
    if (nroCliente) {
      loadCliente();
    } else {
      // Para nuevos clientes, establecer fechaAlta, tipoCliente y cuenta contable por defecto
      const hoy = new Date().toISOString().split('T')[0];
      
      // Obtener próximo número de cliente
      clienteService.getProximoNumero().then(proximoNumero => {
        setFormData(prev => ({
          ...prev,
          nroCliente: proximoNumero,
          fechaAlta: hoy,
          tipoCliente: 'A'
        }));
      }).catch(() => {
        // Si no se puede obtener el próximo número, usar valores por defecto
        setFormData(prev => ({
          ...prev,
          fechaAlta: hoy,
          tipoCliente: 'A'
        }));
      });
      
      // Cargar estrategia para obtener cuenta contable sugerida
      estrategiaService.getEstrategia(1).then((estrategia: any) => {
        if (estrategia?.clientes) {
          setFormData(prev => ({
            ...prev,
            nroCuenta: estrategia.clientes
          }));
          // Cargar información de la cuenta contable
          imputableService.getImputable(estrategia.clientes).then(imputable => {
            if (imputable) {
              setImputableSeleccionado({
                nroCuenta: imputable.nroCuenta,
                nomCuenta: cleanString(imputable.nomCuenta),
              });
            }
          }).catch(() => {
            // Si no se puede cargar, no es crítico
          });
        }
      }).catch(() => {
        // Si no se puede cargar la estrategia, no es crítico
      });
      
      // Cargar información del vendedor por defecto (1)
      vendedorService.getVendedor(1).then(vendedor => {
        if (vendedor) {
          setVendedorSeleccionado({
            nroVendedor: vendedor.nroVendedor,
            nombreVendedor: vendedor.nombreVendedor,
          });
        }
      }).catch(() => {
        // Si no se puede cargar, no es crítico
      });
      
      // Cargar información del cobrador por defecto (1)
      vendedorService.getVendedor(1).then(cobrador => {
        if (cobrador) {
          setCobradorSeleccionado({
            nroVendedor: cobrador.nroVendedor,
            nombreVendedor: cobrador.nombreVendedor,
          });
        }
      }).catch(() => {
        // Si no se puede cargar, no es crítico
      });
      
      // Cargar información de la ruta por defecto (1)
      rutaService.getRuta(1).then(ruta => {
        if (ruta) {
          setRutaSeleccionada({
            codigoRuta: ruta.codigoRuta,
            nombreRuta: cleanString(ruta.nombreRuta),
          });
        }
      }).catch(() => {
        // Si no se puede cargar, no es crítico
      });
    }
  }, [nroCliente, loadCliente]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const numericFields = new Set([
      'nroCliente','codigoCiudad','credito','ctaMaestra','nroCuenta','deposito','vendedor','nroCobrador','vtoComprob','nroExpreso','reDespacho','porcDesc','porcDesc2','porcDesc3'
    ]);
    if (numericFields.has(name)) {
      const parsed = value === '' ? (null as any) : Number(value);
      setFormData(prev => ({ ...prev, [name]: parsed } as any));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectLocalidad = (localidad: Localidad) => {
    setLocalidadSeleccionada(localidad);
    setFormData(prev => ({
      ...prev,
      codigoCiudad: localidad.codigoCiudad,
      nombreCiudad: localidad.nombreCiudad?.trim() || '',
    }));
  };

  const handleSelectListaPrecio = (listaPrecio: ListaPrecio) => {
    setListaPrecioSeleccionada({
      codigoLista: listaPrecio.codigoLista.trim(),
      descripcion: listaPrecio.descripcion?.trim() || '',
    });
    setFormData(prev => ({ ...prev, nroLista: listaPrecio.codigoLista.trim() }));
  };

  const handleSelectVendedor = (vendedor: Vendedor) => {
    setVendedorSeleccionado({
      nroVendedor: vendedor.nroVendedor,
      nombreVendedor: vendedor.nombreVendedor,
    });
    setFormData(prev => ({ ...prev, vendedor: vendedor.nroVendedor }));
  };

  const handleSelectCobrador = (vendedor: Vendedor) => {
    setCobradorSeleccionado({
      nroVendedor: vendedor.nroVendedor,
      nombreVendedor: vendedor.nombreVendedor,
    });
    setFormData(prev => ({ ...prev, nroCobrador: vendedor.nroVendedor }));
  };

  const handleSelectImputable = (imputable: Imputable) => {
    setImputableSeleccionado({
      nroCuenta: imputable.nroCuenta,
      nomCuenta: cleanString(imputable.nomCuenta),
    });
    setFormData(prev => ({ ...prev, nroCuenta: imputable.nroCuenta }));
  };

  const handleSelectUbicacion = (ubicacion: IUbicacion) => {
    setUbicacionSeleccionada({
      nroUbicacion: ubicacion.nroUbicacion,
      nombreUbicacion: cleanString(ubicacion.nombreUbicacion),
    });
    setFormData(prev => ({ ...prev, deposito: ubicacion.nroUbicacion }));
  };

  const handleSelectTransporte = (transporte: Transporte) => {
    const nroExpreso = transporte.nroExpreso ? (typeof transporte.nroExpreso === 'string' ? Number(transporte.nroExpreso) : transporte.nroExpreso) : null;
    setTransporteSeleccionado({
      nroExpreso: transporte.nroExpreso,
      nombre: cleanString(transporte.nombre),
      nombreExpreso: cleanString(transporte.nombreExpreso),
    });
    setFormData(prev => ({ ...prev, nroExpreso: nroExpreso }));
  };

  const handleSelectRedespacho = (transporte: Transporte) => {
    const nroExpreso = transporte.nroExpreso ? (typeof transporte.nroExpreso === 'string' ? Number(transporte.nroExpreso) : transporte.nroExpreso) : null;
    setRedespachoSeleccionado({
      nroExpreso: transporte.nroExpreso,
      nombre: cleanString(transporte.nombre),
      nombreExpreso: cleanString(transporte.nombreExpreso),
    });
    setFormData(prev => ({ ...prev, reDespacho: nroExpreso }));
  };

  const handleSelectRuta = (ruta: Ruta) => {
    setRutaSeleccionada({
      codigoRuta: ruta.codigoRuta,
      nombreRuta: cleanString(ruta.nombreRuta),
    });
    setFormData(prev => ({ ...prev, codigoRuta: ruta.codigoRuta ?? null }));
  };

  const handleDireccionesUpdated = async () => {
    if (nroCliente) {
      try {
        const direccionesData = await direccionClienteService.getDirecciones(Number(nroCliente));
        setDirecciones(direccionesData || []);
        
        // Cargar información de localidades para las direcciones
        const localidadesMap = new Map<string, Localidad>();
        for (const dir of direccionesData || []) {
          if (dir.codigoCiudad) {
            try {
              const loc = await localidadService.getLocalidad(dir.codigoCiudad);
              localidadesMap.set(cleanString(dir.codigoDireccion), loc);
            } catch (error) {
              // Si no se puede cargar la localidad, no es crítico
              console.error(`Error al cargar localidad ${dir.codigoCiudad}:`, error);
            }
          }
        }
        setLocalidadesDirecciones(localidadesMap);
        
        // Actualizar las tarjetas (Facturación, Entrega, Resumen) con los datos recién cargados
        const codigoDirFacturacion = formData.codigoDireccion ? cleanString(formData.codigoDireccion) : null;
        const codigoDirEnvio = formData.codigoDireccionEnvio ? cleanString(formData.codigoDireccionEnvio) : null;
        const codigoDirResumen = formData.codigoDireccionResumen ? cleanString(formData.codigoDireccionResumen) : null;
        if (codigoDirFacturacion) {
          const dir = direccionesData?.find(d => cleanString(d.codigoDireccion) === codigoDirFacturacion);
          setDireccionFacturacion(dir || null);
        } else {
          setDireccionFacturacion(null);
        }
        if (codigoDirEnvio) {
          const dir = direccionesData?.find(d => cleanString(d.codigoDireccion) === codigoDirEnvio);
          setDireccionEnvio(dir || null);
        } else {
          setDireccionEnvio(null);
        }
        if (codigoDirResumen) {
          const dir = direccionesData?.find(d => cleanString(d.codigoDireccion) === codigoDirResumen);
          setDireccionResumen(dir || null);
        } else {
          setDireccionResumen(null);
        }
      } catch (error) {
        console.error('Error al actualizar direcciones:', error);
      }
    }
  };

  const handleSelectDireccion = (tipo: 'facturacion' | 'envio' | 'resumen', codigoDireccion: string | null) => {
    // Buscar la dirección completa en el array de direcciones
    const direccion = codigoDireccion 
      ? direcciones.find(d => cleanString(d.codigoDireccion) === cleanString(codigoDireccion)) || null
      : null;
    
    if (tipo === 'facturacion') {
      setDireccionFacturacion(direccion);
      const updates: Partial<ClienteFormData> = { codigoDireccion: codigoDireccion };
      if (direccion) {
        updates.direccion = cleanString(direccion.direccion);
        updates.codigoCiudad = direccion.codigoCiudad ?? null;
        if (direccion.codigoCiudad) {
          localidadService.getLocalidad(direccion.codigoCiudad)
            .then(loc => setLocalidadSeleccionada(loc))
            .catch(() => {});
        } else {
          setLocalidadSeleccionada(null);
        }
      } else {
        updates.direccion = '';
        updates.codigoCiudad = null;
        setLocalidadSeleccionada(null);
      }
      setFormData(prev => ({ ...prev, ...updates }));
    } else if (tipo === 'envio') {
      setDireccionEnvio(direccion);
      const updates: Partial<ClienteFormData> = { codigoDireccionEnvio: codigoDireccion };
      if (direccion) {
        updates.direccionEnvio = cleanString(direccion.direccion);
        updates.codigoCiudadEnvio = direccion.codigoCiudad ?? null;
      } else {
        updates.direccionEnvio = '';
        updates.codigoCiudadEnvio = null;
      }
      setFormData(prev => ({ ...prev, ...updates }));
    } else if (tipo === 'resumen') {
      setDireccionResumen(direccion);
      const updates: Partial<ClienteFormData> = { codigoDireccionResumen: codigoDireccion };
      if (direccion) {
        updates.direccionResumen = cleanString(direccion.direccion);
        updates.codigoCiudadResumen = direccion.codigoCiudad ?? null;
      } else {
        updates.direccionResumen = '';
        updates.codigoCiudadResumen = null;
      }
      setFormData(prev => ({ ...prev, ...updates }));
    }
    
    // Recargar localidades si la dirección tiene ciudad
    if (direccion?.codigoCiudad) {
      localidadService.getLocalidad(direccion.codigoCiudad)
        .then(loc => {
          setLocalidadesDirecciones(prev => {
            const newMap = new Map(prev);
            newMap.set(cleanString(direccion.codigoDireccion), loc);
            return newMap;
          });
        })
        .catch(() => {
          // Si no se puede cargar, no es crítico
        });
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      const requiredMissing: string[] = [];
      if (!formData.nroCliente || Number(formData.nroCliente) <= 0) requiredMissing.push('Código');
      if (!String(formData.nombre || '').trim()) requiredMissing.push('Nombre');
      if (!String(formData.tipoIva || '').trim()) requiredMissing.push('Tipo IVA');
      if (!String(formData.tipoCliente || '').trim()) requiredMissing.push('Tipo Cliente');
      if (!formData.codigoCiudad || Number(formData.codigoCiudad) <= 0) requiredMissing.push('Ciudad');
      if (!formData.nroCliente && !String(formData.direccion || '').trim()) requiredMissing.push('Dirección');
      if (!formData.nroCuenta || Number(formData.nroCuenta) <= 0) requiredMissing.push('N° Cuenta');
      if (!String(formData.condVta || '').trim()) requiredMissing.push('Condición de Venta');
      if (!formData.fechaAlta) requiredMissing.push('Fecha Alta');
      if (!formData.codigoRuta) requiredMissing.push('Código de Ruta (o Zona)');
      if (requiredMissing.length) {
        enqueueSnackbar(`Complete los campos obligatorios: ${requiredMissing.join(', ')}`, { variant: 'warning' });
        setSaving(false);
        return;
      }
      const email = String(formData.email || '').trim();
      const emailPersonal = String(formData.emailPersonal || '').trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
      if (email && !emailRegex.test(email)) {
        enqueueSnackbar('El email tiene formato inválido (ej: nombre@dominio.com).', { variant: 'error' });
        setSaving(false);
        return;
      }
      if (emailPersonal && !emailRegex.test(emailPersonal)) {
        enqueueSnackbar('El email personal tiene formato inválido (ej: nombre@dominio.com).', { variant: 'error' });
        setSaving(false);
        return;
      }

      // La API espera dni como decimal? (número o null), no string
      const dniPayload = (() => {
        const s = String(formData.dni ?? '').trim();
        if (!s) return null;
        const n = Number(s);
        return Number.isFinite(n) && n >= 0 ? n : null;
      })();

      const payload: any = {
        ...formData,
        nroCliente: Number(formData.nroCliente),
        deposito: Number(formData.deposito ?? 0),
        dni: dniPayload,
        fechaAlta: toIso(formData.fechaAlta ?? null),
        fechaBaja: toIso(formData.fechaBaja ?? null),
        email: email.toLowerCase(),
        emailPersonal: emailPersonal.toLowerCase() || null,
        whatsapp: String(formData.whatsapp || '').trim() || null
      };

      let nroClienteGuardado = nroCliente ? Number(nroCliente) : null;
      
      if (nroClienteGuardado) {
        await clienteService.updateCliente(nroClienteGuardado, payload);
      } else {
        const nuevoCliente = await clienteService.createCliente(payload);
        nroClienteGuardado = nuevoCliente.nroCliente;
      }

      // Las direcciones asignadas (codigoDireccion, codigoDireccionEnvio, codigoDireccionResumen)
      // se guardan directamente en el objeto cliente, ya están incluidas en el payload

      enqueueSnackbar('Cliente guardado exitosamente', { variant: 'success' });
      navigate('/ventas/clientes');
    } catch (error: any) {
      let mostrado = false;
      const data = error?.response?.data;
      
      // Manejar errores de validación del backend
      if (data?.errors && typeof data.errors === 'object') {
        let detalle = '';
        for (const campo in data.errors) {
          if (Array.isArray(data.errors[campo])) {
            const nombreCampo = campo.charAt(0).toLowerCase() + campo.slice(1);
            detalle += `${nombreCampo}: ${data.errors[campo].join('; ')}\n`;
          } else if (typeof data.errors[campo] === 'string') {
            detalle += `${campo}: ${data.errors[campo]}\n`;
          }
        }
        if (detalle) {
          enqueueSnackbar(`Error en los datos: ${detalle.trim()}`, { variant: 'error', autoHideDuration: 12000 });
          mostrado = true;
        }
      }
      
      // Manejar mensajes directos del backend
      let messageText = '';
      if (data?.message) {
        messageText = String(data.message);
      } else if (data && typeof data === 'string') {
        messageText = data;
      } else if (error?.message) {
        messageText = String(error.message);
      }
      
      if (!mostrado && messageText) {
        // Casos especiales conocidos
        if (
          messageText.includes('PRIMARY KEY') ||
          messageText.toLowerCase().includes('duplicate') ||
          messageText.includes('Violation of PRIMARY KEY constraint')
        ) {
          enqueueSnackbar(`El cliente con código ${formData.nroCliente} ya existe.`, { variant: 'error' });
          mostrado = true;
        } else {
          // Mostrar el mensaje del backend directamente
          enqueueSnackbar(messageText, { variant: 'error', autoHideDuration: 10000 });
          mostrado = true;
        }
      }
      
      // Si no se mostró nada, mostrar mensaje genérico
      if (!mostrado) {
        enqueueSnackbar('Error al guardar el cliente', { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', p: { xs: 2, sm: 3 } }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', p: 3 }}>
        <Typography 
          variant="h5" 
          gutterBottom
          sx={{
            fontWeight: 600,
            color: '#1976d2',
            mb: 3
          }}
        >
          {nroCliente ? 'Editar Cliente' : 'Nuevo Cliente'}
        </Typography>
        <form onSubmit={handleSubmit}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange} 
              aria-label="pestañas de cliente"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  '&.Mui-selected': {
                    color: '#1976d2',
                    fontWeight: 600,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#1976d2',
                },
              }}
            >
              <Tab label="General" />
              <Tab label="Direcciones" />
              <Tab label="Comercial" />
              <Tab label="Extras" />
              <Tab label="Contacto" />
            </Tabs>
          </Box>

        {/* Pestaña General */}
        {currentTab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField 
                name="nroCliente" 
                label="N° Cliente" 
                type="number" 
                value={formData.nroCliente} 
                onChange={handleChange} 
                fullWidth 
                required 
                disabled={!!nroCliente} 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <TextField 
                name="nombre" 
                label="Nombre" 
                value={formData.nombre} 
                onChange={handleChange} 
                fullWidth 
                required 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Tipo IVA</InputLabel>
                <Select
                  name="tipoIva"
                  value={formData.tipoIva || ''}
                  label="Tipo IVA"
                  required
                  onChange={(e) => setFormData(prev => ({ ...prev, tipoIva: e.target.value }))}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="">Seleccione...</MenuItem>
                  <MenuItem value="I">Responsable Inscripto</MenuItem>
                  <MenuItem value="C">Consumidor Final</MenuItem>
                  <MenuItem value="M">Monotributo</MenuItem>
                  <MenuItem value="E">Exento</MenuItem>
                  <MenuItem value="N">No Categorizado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField 
                name="cuit" 
                label="CUIT" 
                value={formData.cuit || ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField 
                name="dni" 
                label="DNI" 
                value={formData.dni || ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                name="email" 
                label="Email" 
                value={formData.email || ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                name="razonSocial" 
                label="Razón Social" 
                value={formData.razonSocial || ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                name="nroCuenta"
                label="N° Cuenta"
                value={imputableSeleccionado ? `${imputableSeleccionado.nroCuenta} - ${imputableSeleccionado.nomCuenta?.trim() || ''}` : (formData.nroCuenta || '')}
                fullWidth 
                required
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenImputableSearch(true)}
                        edge="end"
                        color="primary"
                        title="Buscar cuenta contable"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        )}

        {/* Pestaña Direcciones */}
        {currentTab === 1 && (
          <Grid container spacing={3}>
            {/* Botón para gestionar direcciones: solo al editar un cliente existente */}
            {nroCliente && (
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
                  <Button
                    variant="contained"
                    startIcon={<SettingsIcon />}
                    onClick={() => setOpenDireccionesDialog(true)}
                  >
                    Gestionar Direcciones
                  </Button>
                </Box>
              </Grid>
            )}
            {!nroCliente && (
              <Grid item xs={12}>
                <Box mb={2} p={2} borderRadius={2} border="1px solid #e0e0e0" bgcolor="#fafafa">
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        name="codigoDireccion"
                        label="Código de dirección"
                        value={formData.codigoDireccion || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        helperText="Ej: CASA CENTRAL, SUCURSAL1, FISCAL, etc. (Si se deja vacío, el sistema usará FISCAL)"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: '#ffffff',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="direccion"
                        label="Domicilio"
                        value={formData.direccion || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: '#ffffff',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Ciudad"
                        value={localidadSeleccionada ? [localidadSeleccionada.nombreCiudad, localidadSeleccionada.nombreProvincia].filter(Boolean).join(', ') || '' : (formData.nombreCiudad || '')}
                        fullWidth
                        margin="normal"
                        required
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setOpenLocalidadSearch(true)}
                                edge="end"
                                color="primary"
                                title="Buscar localidad"
                                sx={{ borderRadius: 2 }}
                              >
                                <ArrowDropDownIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: '#ffffff',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="telefono1"
                        label="Teléfono"
                        value={formData.telefono1 || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: '#ffffff',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="telefono2"
                        label="Teléfono alternativo"
                        value={formData.telefono2 || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: '#ffffff',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="email"
                        label="Email"
                        type="email"
                        value={formData.email || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: '#ffffff',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Con estos datos se creará automáticamente la dirección de <strong>Facturación</strong>, <strong>Entrega</strong> y <strong>Estado de Cuenta</strong> cuando guardes el cliente.
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}

            {/* Controles de dirección: solo al editar un cliente existente */}
            {nroCliente && (
              <>
            {/* Card de Dirección de Facturación */}
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%',
                  border: direccionFacturacion ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    '&:hover': {
                    boxShadow: 3,
                  },
                }}
              >
                <CardContent>
                  <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" color="primary">
                        <LocationOnIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                        Facturación
                      </Typography>
                      {direccionFacturacion && (
                        <Chip 
                          label="Desasignar" 
                          color="error" 
                          size="small"
                          onClick={() => {
                            handleSelectDireccion('facturacion', null);
                            enqueueSnackbar('Dirección desasignada de Facturación', { variant: 'info' });
                          }}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'error.dark',
                            }
                          }}
                        />
                      )}
                    </Box>
                    <Divider />
                    {direccionFacturacion ? (
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Código:</strong>
                          </Typography>
                          <Chip 
                            label={cleanString(direccionFacturacion.codigoDireccion)} 
                            color="primary" 
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Dirección:</strong>
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {cleanString(formData.direccion) || cleanString(direccionFacturacion.direccion)}
                        </Typography>
                        {(() => {
                          const loc = localidadSeleccionada
                            || localidadesDirecciones.get(cleanString(direccionFacturacion.codigoDireccion));
                          const line = formatLocalidadLine(loc);
                          return line ? (
                            <Typography variant="body2" color="text.secondary">{line}</Typography>
                          ) : null;
                        })()}
                        {(() => {
                          const t1 = cleanString(formData.telefono1);
                          const t2 = cleanString(formData.telefono2);
                          const line = [t1, t2].filter(Boolean).join(' / ');
                          if (line) {
                            return (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                <strong>Teléfono:</strong> {line}
                              </Typography>
                            );
                          }
                          const d1 = cleanString(direccionFacturacion.telefono1);
                          const d2 = cleanString(direccionFacturacion.telefono2);
                          const fallback = [d1, d2].filter(Boolean).join(' / ');
                          return fallback ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              <strong>Teléfono:</strong> {fallback}
                            </Typography>
                          ) : null;
                        })()}
                        {(cleanString(formData.email) || cleanString(direccionFacturacion.email)) && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Email:</strong> {cleanString(formData.email) || direccionFacturacion.email}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No hay dirección asignada
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        if (!nroCliente) {
                          enqueueSnackbar('Debe guardar el cliente primero', { variant: 'warning' });
                          return;
                        }
                        setOpenDireccionesDialog(true);
                      }}
                      disabled={!nroCliente || direcciones.length === 0}
                    >
                      {direccionFacturacion ? 'Cambiar' : 'Asignar'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Card de Dirección de Entrega */}
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%',
                  border: direccionEnvio ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  '&:hover': {
                    boxShadow: 3,
                  },
                }}
              >
                <CardContent>
                  <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" color="primary">
                        <LocationOnIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                        Entrega
                      </Typography>
                      {direccionEnvio && (
                        <Chip 
                          label="Desasignar" 
                          color="error" 
                          size="small"
                          onClick={() => {
                            handleSelectDireccion('envio', null);
                            enqueueSnackbar('Dirección desasignada de Entrega', { variant: 'info' });
                          }}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'error.dark',
                            }
                          }}
                        />
                      )}
                    </Box>
                    <Divider />
                    {direccionEnvio ? (
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Código:</strong>
                          </Typography>
                          <Chip 
                            label={cleanString(direccionEnvio.codigoDireccion)} 
                            color="primary" 
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Dirección:</strong>
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {entregaIgualAFacturacion
                            ? (cleanString(formData.direccion) || cleanString(direccionEnvio.direccion))
                            : (cleanString(formData.direccionEnvio) || cleanString(direccionEnvio.direccion))}
                        </Typography>
                        {(() => {
                          const loc = entregaIgualAFacturacion
                            ? (localidadSeleccionada
                                || localidadesDirecciones.get(cleanString(formData.codigoDireccion || direccionEnvio.codigoDireccion)))
                            : localidadesDirecciones.get(cleanString(direccionEnvio.codigoDireccion));
                          const line = formatLocalidadLine(loc);
                          return line ? (
                            <Typography variant="body2" color="text.secondary">{line}</Typography>
                          ) : null;
                        })()}
                        {(() => {
                          const t1 = entregaIgualAFacturacion ? cleanString(formData.telefono1) : cleanString(direccionEnvio.telefono1);
                          const t2 = entregaIgualAFacturacion ? cleanString(formData.telefono2) : cleanString(direccionEnvio.telefono2);
                          const line = [t1, t2].filter(Boolean).join(' / ');
                          return line ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              <strong>Teléfono:</strong> {line}
                            </Typography>
                          ) : null;
                        })()}
                        {(entregaIgualAFacturacion
                          ? (cleanString(formData.email) || cleanString(direccionEnvio.email))
                          : cleanString(direccionEnvio.email)) && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Email:</strong>{' '}
                            {entregaIgualAFacturacion
                              ? (cleanString(formData.email) || direccionEnvio.email)
                              : direccionEnvio.email}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No hay dirección asignada
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        if (!nroCliente) {
                          enqueueSnackbar('Debe guardar el cliente primero', { variant: 'warning' });
                          return;
                        }
                        setOpenDireccionesDialog(true);
                      }}
                      disabled={!nroCliente || direcciones.length === 0}
                    >
                      {direccionEnvio ? 'Cambiar' : 'Asignar'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Card de Dirección de Estado de Cuenta */}
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%',
                  border: direccionResumen ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  '&:hover': {
                    boxShadow: 3,
                  },
                }}
              >
                <CardContent>
                  <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" color="primary">
                        <LocationOnIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                        Estado de Cuenta
                      </Typography>
                      {direccionResumen && (
                        <Chip 
                          label="Desasignar" 
                          color="error" 
                          size="small"
                          onClick={() => {
                            handleSelectDireccion('resumen', null);
                            enqueueSnackbar('Dirección desasignada de Estado de Cuenta', { variant: 'info' });
                          }}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'error.dark',
                            }
                          }}
                        />
                      )}
                    </Box>
                    <Divider />
                    {direccionResumen ? (
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Código:</strong>
                          </Typography>
                          <Chip 
                            label={cleanString(direccionResumen.codigoDireccion)} 
                            color="primary" 
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Dirección:</strong>
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {resumenIgualAFacturacion
                            ? (cleanString(formData.direccion) || cleanString(direccionResumen.direccion))
                            : (cleanString(formData.direccionResumen) || cleanString(direccionResumen.direccion))}
                        </Typography>
                        {(() => {
                          const loc = resumenIgualAFacturacion
                            ? (localidadSeleccionada
                                || localidadesDirecciones.get(cleanString(formData.codigoDireccion || direccionResumen.codigoDireccion)))
                            : localidadesDirecciones.get(cleanString(direccionResumen.codigoDireccion));
                          const line = formatLocalidadLine(loc);
                          return line ? (
                            <Typography variant="body2" color="text.secondary">{line}</Typography>
                          ) : null;
                        })()}
                        {(() => {
                          const t1 = resumenIgualAFacturacion ? cleanString(formData.telefono1) : cleanString(direccionResumen.telefono1);
                          const t2 = resumenIgualAFacturacion ? cleanString(formData.telefono2) : cleanString(direccionResumen.telefono2);
                          const line = [t1, t2].filter(Boolean).join(' / ');
                          return line ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              <strong>Teléfono:</strong> {line}
                            </Typography>
                          ) : null;
                        })()}
                        {(resumenIgualAFacturacion
                          ? (cleanString(formData.email) || cleanString(direccionResumen.email))
                          : cleanString(direccionResumen.email)) && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Email:</strong>{' '}
                            {resumenIgualAFacturacion
                              ? (cleanString(formData.email) || direccionResumen.email)
                              : direccionResumen.email}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No hay dirección asignada
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        if (!nroCliente) {
                          enqueueSnackbar('Debe guardar el cliente primero', { variant: 'warning' });
                          return;
                        }
                        setOpenDireccionesDialog(true);
                      }}
                      disabled={!nroCliente || direcciones.length === 0}
                    >
                      {direccionResumen ? 'Cambiar' : 'Asignar'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
              </>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                name="nroExpreso"
                label="Transporte"
                value={transporteSeleccionado ? `${transporteSeleccionado.nroExpreso || ''} - ${transporteSeleccionado.nombre?.trim() || transporteSeleccionado.nombreExpreso?.trim() || ''}` : (formData.nroExpreso || '')}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenTransporteSearch(true)}
                        edge="end"
                        color="primary"
                        title="Buscar transporte"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="reDespacho"
                label="Redespacho"
                value={redespachoSeleccionado ? `${redespachoSeleccionado.nroExpreso || ''} - ${redespachoSeleccionado.nombre?.trim() || redespachoSeleccionado.nombreExpreso?.trim() || ''}` : (formData.reDespacho || '')}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenRedespachoSearch(true)}
                        edge="end"
                        color="primary"
                        title="Buscar transporte"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        )}

        {/* Pestaña Comercial */}
        {currentTab === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField 
                name="nroLista"
                label="Lista de Precios"
                value={listaPrecioSeleccionada ? `${listaPrecioSeleccionada.codigoLista.trim()} - ${listaPrecioSeleccionada.descripcion?.trim() || ''}` : (formData.nroLista || '')}
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenListaPrecioSearch(true)}
                        edge="end"
                        color="primary"
                        title="Buscar lista de precios"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                name="vendedor"
                label="Vendedor"
                value={vendedorSeleccionado ? `${vendedorSeleccionado.nroVendedor} - ${vendedorSeleccionado.nombreVendedor?.trim() || ''}` : (formData.vendedor || '')}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenVendedorSearch(true)}
                        edge="end"
                        color="primary"
                        title="Buscar vendedor"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                name="nroCobrador"
                label="Cobrador"
                value={cobradorSeleccionado ? `${cobradorSeleccionado.nroVendedor} - ${cobradorSeleccionado.nombreVendedor?.trim() || ''}` : (formData.nroCobrador || '')}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenCobradorSearch(true)}
                        edge="end"
                        color="primary"
                        title="Buscar cobrador"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Condición de Venta</InputLabel>
                <Select
                  name="condVta"
                  value={formData.condVta || ''}
                  label="Condición de Venta"
                  required
                  onChange={(e) => setFormData(prev => ({ ...prev, condVta: e.target.value }))}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="">Seleccione...</MenuItem>
                  <MenuItem value="EFECTIVO">EFECTIVO</MenuItem>
                  <MenuItem value="CONTADO">CONTADO</MenuItem>
                  <MenuItem value="CTA.CTE.">CTA.CTE.</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField 
                name="credito" 
                label="Crédito" 
                type="number" 
                value={formData.credito ?? ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Vto. Comprob.</InputLabel>
                <Select
                  name="vtoComprob"
                  value={formData.vtoComprob ?? ''}
                  label="Días Vto. Comprob."
                  onChange={(e) => setFormData(prev => ({ ...prev, vtoComprob: e.target.value === '' ? null : Number(e.target.value) }))}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="">Seleccione...</MenuItem>
                  <MenuItem value={7}>7 días</MenuItem>
                  <MenuItem value={14}>14 días</MenuItem>
                  <MenuItem value={30}>30 días</MenuItem>
                  <MenuItem value={45}>45 días</MenuItem>
                  <MenuItem value={60}>60 días</MenuItem>
                  <MenuItem value={90}>90 días</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Tipo Cliente</InputLabel>
                <Select
                  name="tipoCliente"
                  value={formData.tipoCliente || ''}
                  label="Tipo Cliente"
                  required
                  onChange={(e) => setFormData(prev => ({ ...prev, tipoCliente: e.target.value }))}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="">Seleccione...</MenuItem>
                  <MenuItem value="A">Activo</MenuItem>
                  <MenuItem value="E">Eventual</MenuItem>
                  <MenuItem value="I">Inactivo</MenuItem>
                  <MenuItem value="S">Suspendido</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField 
                name="ctaMaestra" 
                label="Cuenta Maestra" 
                type="number" 
                value={formData.ctaMaestra ?? ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                name="codigoRuta"
                label="Código de Ruta (o Zona)"
                required
                value={rutaSeleccionada 
                  ? `${rutaSeleccionada.codigoRuta || ''}${rutaSeleccionada.nombreRuta?.trim() ? ' - ' + rutaSeleccionada.nombreRuta.trim() : ''}` 
                  : (formData.codigoRuta ? String(formData.codigoRuta) : '')}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenRutaSearch(true)}
                        edge="end"
                        color="primary"
                        title="Buscar ruta"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField 
                name="porcDesc" 
                label="% Desc 1" 
                type="number" 
                value={formData.porcDesc ?? ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField 
                name="porcDesc2" 
                label="% Desc 2" 
                type="number" 
                value={formData.porcDesc2 ?? ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField 
                name="porcDesc3" 
                label="% Desc 3" 
                type="number" 
                value={formData.porcDesc3 ?? ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Pestaña Extras */}
        {currentTab === 3 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField 
                name="fechaAlta" 
                label="Fecha Alta" 
                type="date" 
                value={formData.fechaAlta ? String(formData.fechaAlta).slice(0,10) : ''} 
                onChange={(e) => setFormData(prev => ({ ...prev, fechaAlta: e.target.value }))} 
                fullWidth 
                required
                margin="normal" 
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                name="fechaBaja" 
                label="Fecha Baja" 
                type="date" 
                value={formData.fechaBaja ? String(formData.fechaBaja).slice(0,10) : ''} 
                onChange={(e) => setFormData(prev => ({ ...prev, fechaBaja: e.target.value }))} 
                fullWidth 
                margin="normal" 
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField 
                name="deposito"
                label="Depósito"
                value={ubicacionSeleccionada ? `${ubicacionSeleccionada.nroUbicacion} - ${ubicacionSeleccionada.nombreUbicacion?.trim() || ''}` : (formData.deposito || '')}
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                  },
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenUbicacionSearch(true)}
                        edge="end"
                        color="primary"
                        title="Buscar ubicación"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField 
                name="nroClienteExterno" 
                label="N° Cliente Externo" 
                value={formData.nroClienteExterno || ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Atributo 1</InputLabel>
                <Select
                  name="atributo1"
                  value={formData.atributo1 || ''}
                  label="Atributo 1"
                  onChange={(e) => setFormData(prev => ({ ...prev, atributo1: e.target.value }))}
                sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="">Seleccione...</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Atributo 2</InputLabel>
                <Select
                  name="atributo2"
                  value={formData.atributo2 || ''}
                  label="Atributo 2"
                  onChange={(e) => setFormData(prev => ({ ...prev, atributo2: e.target.value }))}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="">Seleccione...</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Atributo 3</InputLabel>
                <Select
                  name="atributo3"
                  value={formData.atributo3 || ''}
                  label="Atributo 3"
                  onChange={(e) => setFormData(prev => ({ ...prev, atributo3: e.target.value }))}
                sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="">Seleccione...</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Atributo 4</InputLabel>
                <Select
                  name="atributo4"
                  value={formData.atributo4 || ''}
                  label="Atributo 4"
                  onChange={(e) => setFormData(prev => ({ ...prev, atributo4: e.target.value }))}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="">Seleccione...</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Atributo 5</InputLabel>
                <Select
                  name="atributo5"
                  value={formData.atributo5 || ''}
                  label="Atributo 5"
                  onChange={(e) => setFormData(prev => ({ ...prev, atributo5: e.target.value }))}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  }}
                >
                  <MenuItem value="">Seleccione...</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        {/* Pestaña Contacto */}
        {currentTab === 4 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                name="contacto" 
                label="Contacto" 
                value={formData.contacto || ''} 
                onChange={handleChange} 
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                name="emailPersonal" 
                label="Email" 
                type="email"
                value={formData.emailPersonal || ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                name="whatsapp" 
                label="WhatsApp" 
                value={formData.whatsapp || ''} 
                onChange={handleChange} 
                fullWidth 
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fafafa',
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      bgcolor: '#ffffff',
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        )}

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            disabled={saving}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            {saving ? <CircularProgress size={24} /> : 'Guardar'}
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/ventas/clientes')} 
            disabled={saving}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            Cancelar
          </Button>
        </Box>
      </form>
      </Card>

      <LocalidadSearch
        open={openLocalidadSearch}
        onClose={() => setOpenLocalidadSearch(false)}
        onSelect={handleSelectLocalidad}
      />

      <ListaPrecioSearch
        open={openListaPrecioSearch}
        onClose={() => setOpenListaPrecioSearch(false)}
        onSelect={handleSelectListaPrecio}
      />

      <VendedorSearch
        open={openVendedorSearch}
        onClose={() => setOpenVendedorSearch(false)}
        onSelect={handleSelectVendedor}
      />

      <VendedorSearch
        open={openCobradorSearch}
        onClose={() => setOpenCobradorSearch(false)}
        onSelect={handleSelectCobrador}
      />

      <ImputableSearch
        open={openImputableSearch}
        onClose={() => setOpenImputableSearch(false)}
        onSelect={handleSelectImputable}
      />

      <UbicacionSearch
        open={openUbicacionSearch}
        onClose={() => setOpenUbicacionSearch(false)}
        onSelect={handleSelectUbicacion}
      />

      <TransporteSearch
        open={openTransporteSearch}
        onClose={() => setOpenTransporteSearch(false)}
        onSelect={handleSelectTransporte}
      />

      <TransporteSearch
        open={openRedespachoSearch}
        onClose={() => setOpenRedespachoSearch(false)}
        onSelect={handleSelectRedespacho}
      />

      <RutaSearch
        open={openRutaSearch}
        onClose={() => setOpenRutaSearch(false)}
        onSelect={handleSelectRuta}
      />
      {nroCliente && (
        <DireccionesClienteDialog
          open={openDireccionesDialog}
          onClose={() => setOpenDireccionesDialog(false)}
          nroCliente={Number(nroCliente)}
          onDireccionesUpdated={handleDireccionesUpdated}
          direccionFacturacion={formData.codigoDireccion}
          direccionEnvio={formData.codigoDireccionEnvio}
          direccionResumen={formData.codigoDireccionResumen}
          onAsignarDireccion={handleSelectDireccion}
        />
      )}
    </Box>
  );
};

export default ClienteForm;



